// src/pages/CategoryPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FiFilter, FiGrid, FiList } from 'react-icons/fi';
import ProductCard from '../components/product/ProductCard';
import api from '../services/api';

const CategoryPage = () => {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categoryDetails, setCategoryDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('featured');
  const [showFilters, setShowFilters] = useState(false);

  // Dynamic filter options
  const [availableColors, setAvailableColors] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);

  const [filters, setFilters] = useState({
    price: { min: 0, max: 500000 },
    colors: [],
    sizes: []
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const categoriesRes = await api.get('/categories');
        const allCategories = Array.isArray(categoriesRes) ? categoriesRes : (categoriesRes.categories || []);

        const currentCat = allCategories.find(
          c => c.slug === slug || c.name.toLowerCase().replace(/ /g, '-') === slug
        );

        setCategoryDetails(currentCat || null);

        const productsRes = await api.get('/products');
        const allProducts = Array.isArray(productsRes) ? productsRes : (productsRes.products || []);

        let categoryProducts = allProducts;
        if (currentCat) {
          categoryProducts = allProducts.filter(p => {
            const pCatId = p.category?._id || p.category;
            return pCatId === currentCat._id;
          });
        } else {
          categoryProducts = allProducts.filter(
            p => (p.category?.name && p.category.name.toLowerCase().replace(/ /g, '-') === slug) ||
                 (typeof p.category === 'string' && p.category.toLowerCase().replace(/ /g, '-') === slug)
          );
        }

        setProducts(categoryProducts);
        setFilteredProducts(categoryProducts);
      } catch (err) {
        console.error("Error fetching category data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  // Extract unique colors and sizes from products
  useEffect(() => {
    if (products.length > 0) {
      // Extract colors
      const allColors = products.flatMap(p => p.colors || []).filter(Boolean);
      const uniqueColors = [...new Set(allColors)];
      setAvailableColors(uniqueColors);

      // Extract sizes (handling both string and object types)
      const allSizes = products.flatMap(p => {
        if (Array.isArray(p.sizes)) {
          return p.sizes.map(s => (typeof s === 'object' ? s.name : s));
        }
        return [];
      }).filter(Boolean);
      const uniqueSizes = [...new Set(allSizes)];
      setAvailableSizes(uniqueSizes);
    } else {
      setAvailableColors([]);
      setAvailableSizes([]);
    }
  }, [products]);

  // Filter and sort logic
  useEffect(() => {
    let results = [...products];

    if (filters.price.min > 0 || filters.price.max < 500000) {
      results = results.filter(p => p.price >= filters.price.min && p.price <= filters.price.max);
    }

    if (filters.colors.length > 0) {
      results = results.filter(p => p.colors?.some(c => filters.colors.includes(c)));
    }

    if (filters.sizes.length > 0) {
      results = results.filter(p => p.sizes?.some(s => {
        const sizeName = typeof s === 'object' ? s.name : s;
        return filters.sizes.includes(sizeName);
      }));
    }

    switch (sortBy) {
      case 'price-low':
        results.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        results.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        results.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      default:
        results.sort((a, b) => (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0));
        break;
    }

    setFilteredProducts(results);
  }, [filters, sortBy, products]);

  const toggleFilter = (type, value) => {
    if (type === 'color') {
      setFilters(prev => ({
        ...prev,
        colors: prev.colors.includes(value) ? prev.colors.filter(c => c !== value) : [...prev.colors, value]
      }));
    } else if (type === 'size') {
      setFilters(prev => ({
        ...prev,
        sizes: prev.sizes.includes(value) ? prev.sizes.filter(s => s !== value) : [...prev.sizes, value]
      }));
    }
  };

  const handlePriceChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      price: { ...prev.price, [name]: parseInt(value) || 0 }
    }));
  };

  const clearFilters = () => {
    setFilters({ price: { min: 0, max: 500000 }, colors: [], sizes: [] });
  };

  const categoryName = categoryDetails ? categoryDetails.name : slug.replace(/-/g, ' ').toUpperCase();

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 pt-10 pb-20">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-light uppercase tracking-[4px] text-black">
            {categoryName}
          </h1>
          <p className="text-sm text-gray-500 mt-2">{filteredProducts.length} products</p>
        </div>

        <div className="border-t border-b border-gray-200 py-5 mb-10">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 border border-gray-300 px-5 py-2 text-sm hover:border-black transition"
              >
                <FiFilter size={16} />
                Filter
              </button>
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-black underline"
              >
                Clear all filters
              </button>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-300 px-4 py-2 text-sm outline-none bg-white"
              >
                <option value="featured">Featured</option>
                <option value="price-low">Price Low To High</option>
                <option value="price-high">Price High To Low</option>
                <option value="newest">Newest</option>
              </select>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`border border-gray-300 p-2 ${viewMode === 'grid' ? 'bg-gray-100' : ''}`}
                >
                  <FiGrid size={16} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`border border-gray-300 p-2 ${viewMode === 'list' ? 'bg-gray-100' : ''}`}
                >
                  <FiList size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {showFilters && (
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Price</h4>
                <div className="flex items-center gap-3">
                  <input type="number" name="min" value={filters.price.min} onChange={handlePriceChange} placeholder="Min" className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                  <span className="text-gray-500">—</span>
                  <input type="number" name="max" value={filters.price.max} onChange={handlePriceChange} placeholder="Max" className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                </div>
              </div>

              {availableColors.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Color</h4>
                  <div className="flex flex-wrap gap-2">
                    {availableColors.map(color => (
                      <button
                        key={color}
                        onClick={() => toggleFilter('color', color)}
                        className={`px-3 py-1 text-sm border rounded ${
                          filters.colors.includes(color)
                            ? 'bg-black text-white border-black'
                            : 'bg-white text-gray-700 border-gray-300'
                        }`}
                      >
                        {color}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {availableSizes.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Size</h4>
                  <div className="flex flex-wrap gap-2">
                    {availableSizes.map(size => (
                      <button
                        key={size}
                        onClick={() => toggleFilter('size', size)}
                        className={`px-3 py-1 text-sm border rounded ${
                          filters.sizes.includes(size)
                            ? 'bg-black text-white border-black'
                            : 'bg-white text-gray-700 border-gray-300'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="h-10 w-10 rounded-full border-2 border-gray-300 border-t-black animate-spin"></div>
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-10' : 'grid grid-cols-1 gap-6'}>
            {filteredProducts.length > 0 ? (
              filteredProducts.map(product => (
                <ProductCard key={product._id} product={product} />
              ))
            ) : (
              <div className="col-span-full text-center py-10 text-gray-500">
                No products found matching your criteria.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;